// Create an isolated Handlebars instance so that we don't reference anything from the global instance
const Handlebars = require('handlebars').create()
const {normaliseHashArgs} = require('./hash-args')

// Register Handlebars Helpers

Handlebars.registerHelper('now', require('./helpers/now-helper'))
Handlebars.registerHelper('current_year', require('./helpers/current-year-helper'))
Handlebars.registerHelper('cdnUrl', require('./helpers/cdn-url-helper'))
Handlebars.registerHelper('coalesce', require('./helpers/coalesce-helper'))
Handlebars.registerHelper('default', require('./helpers/default-helper'))
Handlebars.registerHelper('join', require('./helpers/join-helper'))
Handlebars.registerHelper('website_link', require('./helpers/website-link-helper'))
Handlebars.registerHelper('reviews_value', require('./helpers/reviews-value-helper'))
Handlebars.registerHelper('reviews_brick', require('./helpers/reviews-brick-helper'))

// End

/**
 * Resolves any Handlebars bindings found in values of the provided data object.
 *
 * @param {*} data The object to find and Handlebars bindings and resolve them
 * @param {object} [context={}] A data context that is available for bindings
 * @return {Promise<*>}
 */
const resolve = (data, context = {}) => {
    let templateString = JSON.stringify(data)

    // The rich-text editor stores brick tokens in `data-velocity-token` attributes so that we can
    // re-hydrate them back into interactive chips. Those attribute values must NOT be executed by
    // Handlebars (or rewritten into helper output), otherwise the attribute becomes corrupted HTML
    // and bricks can duplicate/nest on reload. We URL-encode the attribute values so they do not
    // match our Handlebars token rewrite regexes.
    const encodeVelocityTokenAttr = (rawValue) => {
        if (!rawValue || !rawValue.includes('{{')) return rawValue
        if (/%7b%7b/i.test(rawValue) || /%7d%7d/i.test(rawValue)) return rawValue
        return encodeURIComponent(rawValue)
    }

    templateString = templateString.replace(
        /data-velocity-token=\\"([^\\"]*)\\"/g,
        (match, rawValue) => `data-velocity-token=\\"${encodeVelocityTokenAttr(rawValue)}\\"`
    )
    templateString = templateString.replace(
        /data-velocity-token='([^']*)'/g,
        (match, rawValue) => `data-velocity-token='${encodeVelocityTokenAttr(rawValue)}'`
    )

    // Rewrite reviews binds into a single helper call so we can support options like:
    // {{reviews.google.group:default.avg precision=1 scope=client}}
    // {{reviews.google.source:ChIJ1234.count short=true}}
    templateString = templateString.replace(
        /\{\{\s*reviews\.google\.(group:[^.\s}]+|source:[^.\s}]+)\.(avg|count|mapsUrl)\s*([^}]*)\}\}/g,
        (match, targetToken, field, extra) => {
            const args = `"google" "${targetToken}" "${field}"`
            const tail = normaliseHashArgs((extra || '').trim())
            return `{{reviews_value ${args}${tail ? ' ' + tail : ''}}}`
        }
    )

    // Rewrite rating stars bricks into a single helper call (supports both legacy `> rating.stars ...`
    // and HTML-escaped `&gt;` forms).
    templateString = templateString.replace(
        /\{\{\s*(?:>|&gt;|&amp;gt;)\s*rating\.stars\b([^}]*)\}\}/g,
        (match, args) => {
            let selectionToken = null
            let remainder = (args || '').trim()

            const sourceTokenMatch = /reviews\.google\.source:([a-zA-Z0-9_-]+)/i.exec(remainder)
            if (sourceTokenMatch) {
                selectionToken = `source:${sourceTokenMatch[1]}`
                remainder = remainder.replace(sourceTokenMatch[0], '').trim()
            }

            const groupTokenMatch = /reviews\.google\.group:([a-zA-Z0-9_-]+)/i.exec(remainder)
            if (!selectionToken && groupTokenMatch) {
                selectionToken = `group:${groupTokenMatch[1]}`
                remainder = remainder.replace(groupTokenMatch[0], '').trim()
            }

            const sourceParamMatch = /\bsource(?:Key)?\s*=\s*['"]?([a-zA-Z0-9_-]+)['"]?/i.exec(remainder)
            if (!selectionToken && sourceParamMatch) {
                selectionToken = `source:${sourceParamMatch[1]}`
                remainder = remainder.replace(sourceParamMatch[0], '').trim()
            }

            const groupParamMatch = /\bgroup(?:Key)?\s*=\s*['"]?([a-zA-Z0-9_-]+)['"]?/i.exec(remainder)
            if (!selectionToken && groupParamMatch) {
                selectionToken = `group:${groupParamMatch[1]}`
                remainder = remainder.replace(groupParamMatch[0], '').trim()
            }

            selectionToken = selectionToken || 'group:default'
            const tail = normaliseHashArgs(remainder)
            return `{{{reviews_brick "google" "${selectionToken}"${tail ? ' ' + tail : ''}}}}`
        }
    )

    const template = Handlebars.compile(templateString)
    try {
        return JSON.parse(template(context))
    } catch (err) {
        return data
    }
}

module.exports = {
    resolve
}
